import { useState, useCallback, useEffect } from "react";

export interface CustomPDF {
  id: string;
  title: string;
  description: string;
  url: string;
  category: string;
  status: "active" | "inactive";
  uploadedAt: string;
}

export interface DownloadLog {
  id: string;
  item: string;
  type: "brochure" | "custom_pdf" | "comparison_guide" | "all_brochures";
  url?: string;
  timestamp: string;
  userAgent?: string;
}

const CUSTOM_PDFS_KEY = "expertadvise_custom_pdfs";
const DOWNLOAD_LOGS_KEY = "expertadvise_download_logs";

function loadFromStorage<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : fallback;
  } catch {
    return fallback;
  }
}

function saveToStorage<T>(key: string, value: T) {
  localStorage.setItem(key, JSON.stringify(value));
}

export function useBrochureManager() {
  const [customPDFs, setCustomPDFs] = useState<CustomPDF[]>(() =>
    loadFromStorage<CustomPDF[]>(CUSTOM_PDFS_KEY, [])
  );
  const [downloadLogs, setDownloadLogs] = useState<DownloadLog[]>(() =>
    loadFromStorage<DownloadLog[]>(DOWNLOAD_LOGS_KEY, [])
  );

  useEffect(() => {
    saveToStorage(CUSTOM_PDFS_KEY, customPDFs);
  }, [customPDFs]);

  useEffect(() => {
    saveToStorage(DOWNLOAD_LOGS_KEY, downloadLogs);
  }, [downloadLogs]);

  const addCustomPDF = useCallback((pdf: Omit<CustomPDF, "id" | "uploadedAt">) => {
    const newPDF: CustomPDF = {
      ...pdf,
      id: `pdf-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      uploadedAt: new Date().toISOString(),
    };
    setCustomPDFs((prev) => [newPDF, ...prev]);
    return newPDF;
  }, []);

  const updateCustomPDF = useCallback((id: string, updates: Partial<Omit<CustomPDF, "id" | "uploadedAt">>) => {
    setCustomPDFs((prev) =>
      prev.map((p) => (p.id === id ? { ...p, ...updates } : p))
    );
  }, []);

  const deleteCustomPDF = useCallback((id: string) => {
    setCustomPDFs((prev) => prev.filter((p) => p.id !== id));
  }, []);

  const logDownload = useCallback((item: string, type: DownloadLog["type"], url?: string) => {
    const log: DownloadLog = {
      id: `dl-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      item,
      type,
      url,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
    };
    setDownloadLogs((prev) => [log, ...prev]);
  }, []);

  const getDownloadStats = useCallback(() => {
    const stats: Record<string, { count: number; lastDownload: string; type: string }> = {};
    downloadLogs.forEach((log) => {
      if (!stats[log.item]) {
        stats[log.item] = { count: 0, lastDownload: log.timestamp, type: log.type };
      }
      stats[log.item].count++;
      if (new Date(log.timestamp) > new Date(stats[log.item].lastDownload)) {
        stats[log.item].lastDownload = log.timestamp;
      }
    });
    return stats;
  }, [downloadLogs]);

  const getRecentDownloads = useCallback((limit = 50) => {
    return downloadLogs.slice(0, limit);
  }, [downloadLogs]);

  const getTypeBreakdown = useCallback(() => {
    const breakdown: Record<string, number> = {};
    downloadLogs.forEach((log) => {
      breakdown[log.type] = (breakdown[log.type] || 0) + 1;
    });
    return breakdown;
  }, [downloadLogs]);

  return {
    customPDFs,
    downloadLogs,
    addCustomPDF,
    updateCustomPDF,
    deleteCustomPDF,
    logDownload,
    getDownloadStats,
    getRecentDownloads,
    getTypeBreakdown,
  };
}